import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import PCTMap from '../components/PCTMap';
import PostsFeed from '../components/UpdatesFeed';
import globalStyles from '../styles/global.module.css';
import styles from './Home.module.css';
import apiConfig from '../config/api';
import mexicoFlag from '../images/mexico.webp';
import canadaFlag from '../images/canada.png';
import { getHikeDay } from '../utils/dateUtils.js';

const Home = () => {
  const [stats, setStats] = useState({
    totalMiles: 2650,
    milesCovered: 0,
    currentLocation: 'Not started',
    currentCoordinates: null,
    lastUpdate: 'N/A'
  });
  const [loading, setLoading] = useState(true);
  const [latestPost, setLatestPost] = useState(null);
  const [allUpdates, setAllUpdates] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the latest stats
        const statsResponse = await fetch(`${apiConfig.baseUrl}/api/stats`);
        const statsData = await statsResponse.json();
        
        if (statsData) {
          setStats({
            totalMiles: 2655,
            milesCovered: statsData.milesCovered || 0,
            currentLocation: statsData.currentLocation || 'Not started',
            currentCoordinates: statsData.currentCoordinates || null,
            lastUpdate: statsData.lastUpdate || 'N/A'
          });
        }
        
        // Fetch all posts to find the latest one with location
        const postsResponse = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.updates}`);
        
        // Check if response is OK before trying to parse JSON
        if (!postsResponse.ok) {
          throw new Error(`Server responded with ${postsResponse.status}: ${postsResponse.statusText}`);
        }
        
        const postsData = await postsResponse.json();
        
        if (postsData && postsData.length > 0) {
          console.log('All posts:', postsData);
          
          // Store all updates
          setAllUpdates(postsData);
          
          // Find the latest post that has location coordinates
          const postWithLocation = postsData.find(post => 
            post.location_coordinates && 
            post.location_coordinates.lat && 
            post.location_coordinates.lng
          );
          
          if (postWithLocation) {
            console.log('Found post with location:', postWithLocation);
            setLatestPost(postWithLocation);
          } else {
            console.log('No posts found with location coordinates');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Layout>
      <div className={styles.twoColumnLayout}>
        {/* Left Column - Map and Stats */}
        <div className={styles.leftColumn}>
          <div className={globalStyles.card}>
            {/* Map Section */}
            <div className={styles.mapContainer}>
              {loading ? (
                <div className={styles.mapPlaceholder}>
                  Map loading...
                </div>
              ) : (
                <PCTMap 
                  currentCoordinates={stats.currentCoordinates}
                  milesCovered={stats.milesCovered}
                  totalMiles={stats.totalMiles}
                  latestUpdate={latestPost}
                  updates={allUpdates}
                />
              )}
            </div>
            
            {/* Progress Section */}
            <div className={styles.progressSection}>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFilled}
                  style={{ width: `${(stats.milesCovered / stats.totalMiles) * 100}%` }}
                />
                <div 
                  className={styles.progressPin}
                  style={{ left: `${(stats.milesCovered / stats.totalMiles) * 100}%` }}
                />
                <div className={styles.trailEndpoints}>
                  <div className={`${styles.flagContainer} ${styles.start}`}>
                    <img src={mexicoFlag} alt="Mexico" className={styles.flagImage} />
                  </div>
                  <div className={`${styles.flagContainer} ${styles.end}`}>
                    <img src={canadaFlag} alt="Canada" className={styles.flagImage} />
                  </div>
                </div>
              </div>
              <p className={styles.progressText}>
                {Math.round((stats.milesCovered / stats.totalMiles) * 100)}% complete
              </p>
            </div>
            
            {/* Stats Grid */}
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <h3>Miles Hiked</h3>
                <p>{stats.milesCovered}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Miles to Go</h3>
                <p>{stats.totalMiles - stats.milesCovered}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Days Hiked</h3>
                <p>{getHikeDay(stats.lastUpdate)}</p>
              </div>
              <div className={styles.statCard}>
                <h3>Miles per Day</h3>
                <p>{(stats.milesCovered / getHikeDay(stats.lastUpdate)).toFixed(1)}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right Column - Activity Feed */}
        <div className={styles.rightColumn}>
          <PostsFeed limit={3} />
        </div>
      </div>
    </Layout>
  );
};

export default Home;
